import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import i18n from '@/i18n';
import {
  courseAppearanceService,
  type CourseMenuItem,
} from '@/services/course-appearance.service';
import { appearanceKeys } from './queryKeys';

/** Reorder per-course menu items optimistically (drag and drop). */
export function useReorderCourseMenuItems(productId: string) {
  const qc = useQueryClient();
  const key = appearanceKeys.menuItems(productId);

  return useMutation({
    mutationFn: (ids: string[]) =>
      courseAppearanceService.reorderMenuItems(productId, ids),
    onMutate: async (ids) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<CourseMenuItem[]>(key);
      if (prev) {
        const byId = new Map(prev.map((i) => [i.id, i]));
        const next = ids
          .map((id, idx) => {
            const i = byId.get(id);
            return i ? { ...i, order: idx } : undefined;
          })
          .filter((i): i is CourseMenuItem => !!i);
        qc.setQueryData<CourseMenuItem[]>(key, next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(key, ctx.prev);
      toast.error(i18n.t('adminAppearance:toasts.reorderMenuError'));
    },
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
  });
}
